import {
  ORDER_STEPS,
  ADMIN_ORDER_STATUS_META,
  ADMIN_PAYMENT_STATUS_META,
} from "../constants/orders";
import { formatDate, formatCurrency } from "./formatters";
import { T } from "../constants/theme";

export function resolveOrderStatus(order) {
  return order.delivery_status || order.status || "pending";
}

export function resolveOrderStage(order) {
  const status = resolveOrderStatus(order);
  const index = ORDER_STEPS.indexOf(status);
  if (index >= 0) return index;
  if (status === "processing") return 0;
  if (status === "pending") return -1;
  if (status === "cancelled" || status === "refunded" || status === "returned")
    return ORDER_STEPS.length - 1;
  return 2;
}

export function resolveOrderLabel(order) {
  const status = resolveOrderStatus(order);
  const labels = {
    pending: "To pay",
    processing: "To ship",
    confirmed: "Confirmed",
    packed: "Packed",
    shipped: "Shipped",
    out_for_delivery: "Out for delivery",
    delivered: "Processed",
    cancelled: "Cancelled",
    refunded: "Refunded",
    returned: "Returned",
  };
  return labels[status] || status.replace(/_/g, " ");
}

export function resolveOrderBadgeColor(order) {
  const status = resolveOrderStatus(order);
  if (status === "pending") return "#c0392b";
  if (status === "processing" || status === "confirmed") return "#e8a020";
  if (status === "packed" || status === "shipped") return "#1a6b45";
  if (status === "out_for_delivery") return "#0f5b8d";
  if (status === "delivered") return "#2d6a4f";
  if (status === "cancelled" || status === "refunded" || status === "returned")
    return "#7a4b3a";
  return "#1a6b45";
}

export function adminOrderStatusMeta(status) {
  return ADMIN_ORDER_STATUS_META[status] || { label: status || "—", color: T.muted };
}

export function adminPaymentStatusMeta(status) {
  return ADMIN_PAYMENT_STATUS_META[status] || { label: status || "—", color: T.muted };
}

export function resolveOrderItems(order) {
  return Array.isArray(order?.items) ? order.items : [];
}

export function resolveOrderItemImage(item) {
  return item?.product_image_url || "/images/product-placeholder.png";
}

export function resolveOrderTimeline(order) {
  const stageIndex = resolveOrderStage(order);
  return ORDER_STEPS.map((step, index) => {
    const label =
      step === "out_for_delivery"
        ? "Out For Delivery"
        : step.charAt(0).toUpperCase() + step.slice(1).replace(/_/g, " ");
    let date = null;
    if (step === "confirmed") date = order.created_at;
    if (step === "shipped") date = order.shipped_at;
    if (step === "delivered") date = order.delivered_at;
    return {
      key: step,
      label,
      date,
      done: stageIndex >= index,
    };
  });
}

export function printOrderInvoice(order) {
  const items = resolveOrderItems(order);
  const win = window.open("", "_blank", "noopener,noreferrer");
  if (!win) return;
  const rows = items
    .map(
      (item) => `
        <tr>
          <td>${item.name}</td>
          <td>${item.qty}</td>
          <td style="text-align:right">${formatCurrency(item.price)}</td>
          <td style="text-align:right">${formatCurrency(item.subtotal || item.price * item.qty)}</td>
        </tr>`,
    )
    .join("");
  win.document.write(`
    <html>
      <head>
        <title>Invoice - Order #${order.id}</title>
        <style>
          body { font-family: Georgia, serif; color: #111110; padding: 40px; }
          h1 { font-size: 24px; margin-bottom: 4px; }
          .muted { color: #888780; margin-bottom: 24px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { text-align: left; padding: 10px 8px; border-bottom: 1px solid #E3E1DA; font-size: 14px; }
          th { text-transform: uppercase; font-size: 11px; letter-spacing: .08em; color: #888780; }
          .totals { margin-top: 24px; width: 320px; margin-left: auto; }
          .totals div { display: flex; justify-content: space-between; padding: 6px 0; font-size: 14px; }
          .totals .grand { font-weight: 700; font-size: 17px; border-top: 1px solid #E3E1DA; padding-top: 10px; margin-top: 6px; }
        </style>
      </head>
      <body>
        <h1>Invoice</h1>
        <div class="muted">Order #${order.id} &middot; ${formatDate(order.created_at)}</div>
        <div><strong>${order.shipping_name || ""}</strong></div>
        <div class="muted">${[order.shipping_line1, order.shipping_line2, order.shipping_city, order.shipping_country].filter(Boolean).join(", ")}</div>
        <table>
          <thead>
            <tr><th>Item</th><th>Qty</th><th style="text-align:right">Unit Price</th><th style="text-align:right">Subtotal</th></tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="totals">
          <div><span>Subtotal</span><span>${formatCurrency(order.subtotal)}</span></div>
          ${order.discount ? `<div><span>Discount</span><span>-${formatCurrency(order.discount)}</span></div>` : ""}
          ${order.shipping_cost ? `<div><span>Shipping</span><span>${formatCurrency(order.shipping_cost)}</span></div>` : ""}
          ${order.tax ? `<div><span>Tax</span><span>${formatCurrency(order.tax)}</span></div>` : ""}
          <div class="grand"><span>Grand Total</span><span>${formatCurrency(order.total)}</span></div>
        </div>
      </body>
    </html>
  `);
  win.document.close();
  win.focus();
  win.print();
}

export function exportOrdersCsv(orders) {
  const headers = [
    "Order ID",
    "Customer",
    "Email",
    "Order Date",
    "Items",
    "Total",
    "Payment Method",
    "Payment Status",
    "Order Status",
  ];
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map(escape).join(",")];
  orders.forEach((o) => {
    const customer = `${o.first_name || ""} ${o.last_name || ""}`.trim() || "—";
    lines.push(
      [
        o.id,
        customer,
        o.email || "—",
        formatDate(o.created_at),
        o.item_count ?? "—",
        o.total,
        o.payment_method || "—",
        o.payment_status || "—",
        o.status || "—",
      ]
        .map(escape)
        .join(","),
    );
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `orders-export-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
